import { useEffect, useState } from "react";
import * as d3 from "d3";
import { db } from "../firebase";
import { getDocs, collection, query, orderBy, getDoc, doc } from "firebase/firestore";
import {
  collectNetworkQuestionGroups,
  findNetworkQuestionGroup,
  formatGroupedOrganizationLabel,
  NetworkQuestionGroup,
} from "../utils/networkQuestionGroups";

// Data types shared with NetworkGraph + debug views ---------------------------
export interface NodeDatum extends d3.SimulationNodeDatum {
  id: string;
  degree?: number;
}

export interface LinkDatum extends d3.SimulationLinkDatum<NodeDatum> {
  source: string | NodeDatum;
  target: string | NodeDatum;
  w?: number;
  how?: string;
  _gIndex?: number;
  _gCount?: number;
  sid?: string;
  tid?: string;
  hows?: string[];
  _hasReverse?: boolean;
}

export type RawNomEdge = {
  a: string;
  b: string;
  respIndex: number;
  hows: string[];
  fieldVals: Record<string, string[]>;
};

export type Dimension = { name: string; label: string };

export const DIM_COMBINED = "__COMBINED__";
export const HOW_UNLABELED = "(Unlabeled)";

export type ChoiceItem = string | number | { value: string | number; text?: string };

export type SurveyElement = {
  type?: string;
  name?: string;
  title?: string;
  visibleIf?: string;
  firestoreListId?: string;
  dataListId?: string;
  elements?: SurveyElement[];
  templateElements?: SurveyElement[];
  choices?: ChoiceItem[];
  rateValues?: ChoiceItem[];
  rateMin?: number;
  rateMax?: number;
  rateStep?: number;
};

export type SurveyJSON = {
  title?: string;
  pages?: { name?: string; elements?: SurveyElement[] }[];
};

export type SurveyResponseRecord = {
  id: string;
  answers: any;
  submittedAt?: string | Date;
  createdAt?: string | Date;
};

// Helper functions -----------------------------------------------------------
export const friendlyLabel = (name?: string) => {
  if (!name) return "";
  const spaced = name
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_\-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

export function findElementByName(survey: SurveyJSON | undefined, name: string): SurveyElement | undefined {
  if (!survey?.pages) return undefined;
  const stack: SurveyElement[] = [];
  survey.pages.forEach((p) => p.elements?.forEach((e) => stack.push(e)));
  while (stack.length) {
    const el = stack.pop()!;
    if (el?.name === name) return el;
    el?.elements?.forEach((child) => stack.push(child));
    el?.templateElements?.forEach((child) => stack.push(child));
  }
  return undefined;
}

const choiceListToMap = (list?: ChoiceItem[]): Map<string, string> | undefined => {
  if (!list || !list.length) return undefined;
  const map = new Map<string, string>();
  list.forEach((c) => {
    if (typeof c === "string" || typeof c === "number") {
      const key = String(c);
      map.set(key, key);
    } else if (c && typeof c === "object" && "value" in c) {
      const key = String(c.value);
      map.set(key, c.text ?? key);
    }
  });
  return map.size ? map : undefined;
};

const ratingRangeToMap = (el: SurveyElement): Map<string, string> | undefined => {
  const min = typeof el.rateMin === "number" ? el.rateMin : undefined;
  const max = typeof el.rateMax === "number" ? el.rateMax : undefined;
  if (min === undefined || max === undefined) return undefined;
  const stepRaw = typeof el.rateStep === "number" && el.rateStep > 0 ? el.rateStep : 1;
  const step = stepRaw || 1;
  const map = new Map<string, string>();
  const count = Math.floor((max - min) / step + 1);
  if (!Number.isFinite(count) || count <= 0) return undefined;
  for (let i = 0; i < count; i++) {
    const value = min + i * step;
    if (value > max + 1e-9) break;
    const normalized = Number.isInteger(value) ? String(value) : value.toFixed(2);
    map.set(normalized, normalized);
  }
  if (!map.size) {
    const fallbackMin = String(min);
    const fallbackMax = String(max);
    map.set(fallbackMin, fallbackMin);
    if (fallbackMax !== fallbackMin) map.set(fallbackMax, fallbackMax);
  }
  return map;
};

const norm = (s: string) => s.replace(/\s+/g, " ").trim();

const coalesceOther = (val: string | undefined, comment?: unknown): string => {
  const v = (val ?? "").trim();
  if (!v) return "";
  const isOther = /^other\b|please\s*specify/i.test(v);
  const c = (typeof comment === "string" ? comment : "").trim();
  if (isOther) return c || "";
  return v;
};

const getComment = (container: any, baseName: string): string => {
  const a = container?.[`${baseName}-Comment`];
  const b = container?.[`${baseName}Comment`];
  return (typeof a === "string" ? a : typeof b === "string" ? b : "") as string;
};

export const getAnswerVals = (resp: any, key: string): string[] => {
  const comment = getComment(resp, key);
  const val = resp?.[key];

  if (typeof val === "string") {
    const out = coalesceOther(val, comment);
    return out ? [out] : [];
  }

  if (typeof val === "number") {
    return [String(val)];
  }

  if (typeof val === "boolean") {
    return [val ? "Yes" : "No"];
  }

  if (Array.isArray(val) && val.every((x) => typeof x === "string")) {
    const uniq = new Set<string>();
    (val as string[]).forEach((v) => {
      const out = coalesceOther(v, comment);
      if (out) uniq.add(out);
    });
    return Array.from(uniq);
  }

  const found: string[] = [];
  const push = (s: string | undefined) => {
    const out = coalesceOther(s ?? "", comment);
    if (out && !found.includes(out)) found.push(out);
  };

  if (Array.isArray(val)) {
    val.forEach((item: any) => {
      if (item && typeof item === "object" && typeof item[key] === "string") push(item[key]);
    });
    if (found.length) return found;
  }
  Object.keys(resp || {}).forEach((k) => {
    const arr = resp[k];
    if (Array.isArray(arr)) {
      arr.forEach((item: any) => {
        if (item && typeof item === "object" && typeof item[key] === "string") push(item[key]);
      });
    }
  });
  return found;
};

export const getGroupedAnswerVals = (container: any, group?: NetworkQuestionGroup): string[] => {
  if (!group) return [];
  const out: string[] = [];
  const seen = new Set<string>();
  group.members.forEach((member) => {
    getAnswerVals(container, member.name).forEach((value) => {
      const labelled = formatGroupedOrganizationLabel(value, member.country);
      if (!labelled || seen.has(labelled)) return;
      seen.add(labelled);
      out.push(labelled);
    });
  });
  return out;
};

const pickOrgField = (panelItem: any, howFieldNames: Set<string>): string | undefined => {
  if (!panelItem || typeof panelItem !== "object") return undefined;
  const keys = Object.keys(panelItem);
  let bestKey: string | undefined;
  let bestScore = -Infinity;
  for (const k of keys) {
    const v = panelItem[k];
    if (typeof v !== "string" || !v.trim()) continue;
    if (howFieldNames.has(k)) continue;
    if (/(other|comment)/i.test(k)) continue;
    let score = 0;
    if (/(^|[^a-z])(organisation|organization|org)([^a-z]|$)/i.test(k)) score += 50;
    if (/(^|[^a-z])name([^a-z]|$)/i.test(k)) score += 30;
    if (/(work together|what.*work)/i.test(k)) score -= 20;
    score -= Math.min(k.length, 40) * 0.1;
    if (score > bestScore) {
      bestScore = score;
      bestKey = k;
    }
  }
  if (!bestKey) {
    bestKey = keys.find(
      (k) =>
        typeof panelItem[k] === "string" &&
        panelItem[k].trim() &&
        !howFieldNames.has(k) &&
        !/(other|comment)/i.test(k)
    );
  }
  return bestKey;
};

const buildHowChoiceMaps = (
  survey: SurveyJSON | undefined,
  targetPanelName: string
): {
  maps: Record<string, Map<string, string>>;
  howFieldNames: Set<string>;
  labels: Record<string, string>;
  order: Record<string, string[]>;
} => {
  const maps: Record<string, Map<string, string>> = {};
  const howFieldNames = new Set<string>();
  const labels: Record<string, string> = {};
  const order: Record<string, string[]> = {};
  if (!survey) return { maps, howFieldNames, labels, order };

  const panel = findElementByName(survey, targetPanelName);
  const tpls = panel?.templateElements || [];
  const HOW_TYPES = new Set(["checkbox", "tagbox", "radiogroup", "rating"]);

  tpls.forEach((el) => {
    if (!el?.name) return;
    const type = (el.type || "").toLowerCase();
    if (!HOW_TYPES.has(type)) return;
    howFieldNames.add(el.name);
    labels[el.name] = el.title || friendlyLabel(el.name);
    const mapForField =
      choiceListToMap(el.choices) ??
      choiceListToMap(el.rateValues) ??
      (type === "rating" ? ratingRangeToMap(el) : undefined);
    if (mapForField) maps[el.name] = mapForField;
    if (mapForField) order[el.name] = Array.from(mapForField.values());
  });
  return { maps, howFieldNames, labels, order };
};

// Hook -----------------------------------------------------------------------
export function useNetworkData(params: {
  surveyId?: string;
  source?: string;
  target?: string;
  refreshKey?: number;
}) {
  const { surveyId, source, target, refreshKey = 0 } = params;

  const [rawNomEdges, setRawNomEdges] = useState<RawNomEdge[]>([]);
  const [nodes, setNodes] = useState<NodeDatum[]>([]);
  const [dimensions, setDimensions] = useState<Dimension[]>([{ name: DIM_COMBINED, label: "Combined" }]);
  const [dimensionLabels, setDimensionLabels] = useState<Record<string, string>>({});
  const [howChoiceOrder, setHowChoiceOrder] = useState<Record<string, string[]>>({});
  const [surveyJson, setSurveyJson] = useState<SurveyJSON | undefined>();
  const [surveyData, setSurveyData] = useState<any | undefined>();
  const [responses, setResponses] = useState<SurveyResponseRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!surveyId || !source || !target) return;

    let alive = true;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        // Survey JSON for mapping choice text
        let surveyJsonDoc: SurveyJSON | undefined;
        try {
          const docSnap = await getDoc(doc(db, "surveys", surveyId));
          const raw = docSnap.exists() ? docSnap.data() : undefined;
          setSurveyData(raw);
          surveyJsonDoc =
            (raw?.json as SurveyJSON) ??
            (raw?.creatorJson as SurveyJSON) ??
            (raw?.definition as SurveyJSON) ??
            (raw?.schema as SurveyJSON);
        } catch {
          surveyJsonDoc = undefined;
        }

        const { maps: howChoiceMaps, howFieldNames, labels, order } = buildHowChoiceMaps(surveyJsonDoc, target || "");
        const questionGroups = collectNetworkQuestionGroups(surveyJsonDoc);
        const sourceGroup = findNetworkQuestionGroup(surveyJsonDoc, source);
        const targetGroup = findNetworkQuestionGroup(surveyJsonDoc, target);
        const targetPanelOrgGroup =
          questionGroups.find(
            (group) =>
              group.parentName === target &&
              /organi[sz]ation|org/i.test(group.baseName) &&
              /organi[sz]ation|organisation|organization|org/i.test(group.title)
          ) || questionGroups.find((group) => group.parentName === target);
        if (!alive) return;
        setSurveyJson(surveyJsonDoc);
        setDimensionLabels(labels);
        setHowChoiceOrder(order);

        // Load responses
        let responseDocs: SurveyResponseRecord[] = [];
        try {
          const orderedSnap = await getDocs(
            query(collection(db, "surveys", surveyId, "responses"), orderBy("submittedAt", "asc"))
          );
          responseDocs = orderedSnap.docs
            .map((d) => {
              const data = d.data();
              if (data?.deleted === true) return null;
              return {
                id: d.id,
                answers: data.answers || {},
                submittedAt: data.submittedAt,
                createdAt: data.createdAt,
              };
            })
            .filter(Boolean) as SurveyResponseRecord[];
        } catch {
          const snap = await getDocs(collection(db, "surveys", surveyId, "responses"));
          responseDocs = snap.docs
            .map((d) => {
              const data = d.data();
              if (data?.deleted === true) return null;
              return {
                id: d.id,
                answers: data.answers || {},
                submittedAt: data.submittedAt,
                createdAt: data.createdAt,
              };
            })
            .filter(Boolean) as SurveyResponseRecord[];
        }

        if (!alive) return;
        setResponses(responseDocs);

        const answersArr = responseDocs.map((r) => r.answers || {});

        // Build nominations with per-field values
        const nomEdges: RawNomEdge[] = [];
        const nodeSet = new Set<string>();

        answersArr.forEach((ans, respIndex) => {
          const srcs = sourceGroup ? getGroupedAnswerVals(ans, sourceGroup) : getAnswerVals(ans, source);
          const basicTgts = targetGroup ? getGroupedAnswerVals(ans, targetGroup) : getAnswerVals(ans, target);

          if (basicTgts.length > 0) {
            srcs.forEach((a) => {
              basicTgts.forEach((b) => {
                const A = norm(a), B = norm(b);
                if (!A || !B) return;
                nomEdges.push({ a: A, b: B, respIndex, hows: [], fieldVals: {} });
                nodeSet.add(A); nodeSet.add(B);
              });
            });
            return;
          }

          const panel = ans?.[target];
          if (!Array.isArray(panel)) return;

          panel.forEach((item: any) => {
            const groupedOrgVals = getGroupedAnswerVals(item, targetPanelOrgGroup);
            const fallbackOrgVals: string[] = [];
            if (!groupedOrgVals.length) {
              const orgKey = pickOrgField(item, howFieldNames);
              let orgVal = orgKey && typeof item[orgKey] === "string" ? item[orgKey].trim() : "";
              if (orgKey) {
                const orgComment = getComment(item, orgKey);
                orgVal = coalesceOther(orgVal, orgComment);
              }
              if (orgVal) fallbackOrgVals.push(orgVal);
            }
            const orgVals = groupedOrgVals.length ? groupedOrgVals : fallbackOrgVals;
            if (!orgVals.length) return;

            const fieldVals: Record<string, string[]> = {};
            for (const fieldName of Array.from(howFieldNames)) {
              const v = item?.[fieldName];
              const comment = getComment(item, fieldName);
              const outs: string[] = [];
              const pushValue = (raw: unknown) => {
                if (raw === undefined || raw === null) return;
                let rawStr = "";
                if (typeof raw === "string") rawStr = raw;
                else if (typeof raw === "number") rawStr = String(raw);
                else if (typeof raw === "boolean") rawStr = raw ? "true" : "false";
                if (!rawStr.trim()) return;
                const normalized = coalesceOther(rawStr, comment);
                if (!normalized) return;
                const mapped =
                  howChoiceMaps[fieldName]?.get(rawStr) ??
                  howChoiceMaps[fieldName]?.get(normalized) ??
                  normalized;
                outs.push(mapped);
              };

              if (Array.isArray(v)) {
                v.forEach((entry) => pushValue(entry));
              } else if (typeof v === "string" && v.trim()) {
                v
                  .split(/[;,/|]/)
                  .map((s) => s.trim())
                  .filter(Boolean)
                  .forEach((s) => pushValue(s));
              } else if (typeof v === "number" || typeof v === "boolean") {
                pushValue(v);
              }
              if (outs.length) fieldVals[fieldName] = Array.from(new Set(outs));
            }

            const unionSet = new Set<string>();
            Object.values(fieldVals).forEach((arr) => arr.forEach((x) => unionSet.add(x)));
            const hows = Array.from(unionSet);

            srcs.forEach((a) => {
              orgVals.forEach((orgVal) => {
                const A = norm(a), B = norm(orgVal);
                if (!A || !B) return;
                nomEdges.push({ a: A, b: B, respIndex, hows, fieldVals });
                nodeSet.add(A); nodeSet.add(B);
              });
            });
          });
        });

        if (!alive) return;
        setRawNomEdges(nomEdges);

        const dims: Dimension[] = [{ name: DIM_COMBINED, label: "Combined" }];
        const seen: Record<string, boolean> = {};
        nomEdges.forEach((n) => Object.keys(n.fieldVals).forEach((k) => (seen[k] = true)));
        Object.keys(seen).forEach((name) => dims.push({ name, label: labels[name] || friendlyLabel(name) }));
        setDimensions(dims);

        const nodesArr: NodeDatum[] = Array.from(nodeSet).map((id) => ({ id }));
        const deg = new Map<string, number>();
        nomEdges.forEach((n) => {
          deg.set(n.a, (deg.get(n.a) || 0) + 1);
          deg.set(n.b, (deg.get(n.b) || 0) + 1);
        });
        nodesArr.forEach((n) => (n.degree = deg.get(n.id) || 0));
        setNodes(nodesArr);
      } catch (e: any) {
        if (alive) {
          console.error("useNetworkData error:", e);
          setError(e?.message || "Failed to load network data.");
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [surveyId, source, target, refreshKey]);

  return {
    rawNomEdges,
    nodes,
    dimensions,
    dimensionLabels,
    howChoiceOrder,
    surveyJson,
    surveyData,
    responses,
    loading,
    error,
  };
}
